import { useFeatureFlag } from '@/hooks/useFeatureFlag';

export const FULL_INVENTORY_REQUIRED_FIELDS = [
  'freshFrozen',
  'ownerId',
  'status',
] as const;

export const FULL_INVENTORY_LOCKED_FIELDS = [
  'billStatusImp',
  'billingOwnerId',
  'createdByName',
  'createdOn',
  'donor',
  'modifiedByName',
  'modifiedOn',
  'modifiedOnBehalfById',
  'name',
  'scLastHeat',
  'scPreviousHeat',
  'sire',
  'transferDate',
] as const;

export const FULL_INVENTORY_FIELDS = [
  'approvalNumberId',
  'billStatusImp',
  'billingOwnerId',
  'breedId',
  'calfSex',
  'calfType',
  'cane',
  'cellId',
  'cellLineId',
  'clientRecipient',
  'clinicRanchHaulIn',
  'crackedZona',
  'createdByName',
  'createdOn',
  'createdOnBehalfById',
  'creation',
  'dairyCompEmbryoId',
  'dist',
  'donor',
  'donorAge',
  'donorBreedId',
  'donorJpi',
  'donorNetMerit',
  'donorReg',
  'donorTpi',
  'dtPlus',
  'edonorId',
  'embryoOutsideEntryId',
  'embryoPa',
  'embTag',
  'embTagNumber',
  'estDueDate',
  'estrusSync',
  'export',
  'exportTest1',
  'exportTest2',
  'exportTest3',
  'fertilizationId',
  'fertilizationName',
  'flshId',
  'flushIdNumber',
  'flushName',
  'freezeComment',
  'freezeMethodId',
  'freezeTechId',
  'freezeTime',
  'freezer',
  'freshFrozen',
  'freshStrawNo',
  'frozenGrade',
  'frozenStage',
  'gnrhUsedAtImplant',
  'grade',
  'grader65',
  'grader7Id',
  'grader7Name',
  'heat1',
  'holsteinPtanetMerit',
  'id',
  'impId',
  'implantBillingAccountId',
  'implantDate',
  'implantStatusCode',
  'label1',
  'label2',
  'loadById',
  'loadTime',
  'method',
  'modifiedByName',
  'modifiedOn',
  'modifiedOnBehalfById',
  'naabCode',
  'name',
  'oldOwnerId',
  'oldStorageLocationId',
  'origin',
  'originalFreezeSiteId',
  'ownerCode',
  'ownerId',
  'pc1Date',
  'pc1Res',
  'pc2Date',
  'pc2Res',
  'pc3Date',
  'pc3Res',
  'pc4Date',
  'pc4Res',
  'pc5Date',
  'pc5Res',
  'probableDonorId',
  'programName',
  'psi',
  'qc12WellPlates',
  'qcEg10',
  'qcEg10Date',
  'qcEmbHolding',
  'qcEmbHoldingExpDate',
  'qcGunWarmerTemp',
  'qcSterileStrawLotNumber',
  'qcStrawIncubatorNumber',
  'qcStrawLotNumber',
  'qcSucroseDate',
  'qcThawThermosTemp',
  'qcTryPsInDate',
  'qcVitStrawLotNumber',
  'qcehmDate',
  'qcfreezingComment',
  'qual',
  'quantity',
  'recId',
  'receivedOn',
  'recipBirthDate',
  'recipBodyConditionScore',
  'recipBreedId',
  'recipCalvingDate',
  'recipCowHeifer',
  'recipOrderOfTransfer',
  'recipOwnerId',
  'recipRegNum',
  'recipientId',
  'recipientParity',
  'recipientServices',
  'recoveryFirmCode',
  'recoveryFirmName',
  'rightLeft',
  'scLastHeat',
  'scPreviousHeat',
  'semenColCode',
  'semenId',
  'sex',
  'shipCity',
  'shipState',
  'shipToFbd',
  'shipmentId',
  'shipmentName',
  'sire',
  'sireBreedId',
  'sireName',
  'sireCheeseMerit',
  'sireJpi',
  'sireNetMerit',
  'sireTpi',
  'siteId',
  'soldTo',
  'stage',
  'stateCode',
  'status',
  'statusCode',
  'stopId',
  'storageLocationId',
  'storageLocationValidationId',
  'strawDisclaimer',
  'strawNumber',
  'sync',
  'technician1Id',
  'tempEmbryoId',
  'thawByName',
  'thawMethodId',
  'togId',
  'tpi',
  'transferDate',
  'transferFarmLocationId',
  'transferTime',
  'tt',
] as const;

/**
 * Controls that exist only on the outside-embryo flows. They are not embryo
 * datapoints, so they are absent from the workbook, but the outside entry and
 * edit forms cannot function without them.
 */
const OUTSIDE_ONLY_FIELDS = [
  'deyardId',
  'freezeOwnerId',
  'freezeQuantity',
  'implantTierOf',
  'loadDonorPartners',
  'recipientOwnerId',
  'transferOwnerId',
  'transferQuantity',
  'transferSiteId',
] as const;

const allowedFields = new Set<string>([
  ...FULL_INVENTORY_FIELDS,
  ...OUTSIDE_ONLY_FIELDS,
]);

const lockedFields = new Set<string>(FULL_INVENTORY_LOCKED_FIELDS);
const requiredFields = new Set<string>(FULL_INVENTORY_REQUIRED_FIELDS);

export type EmbryoFieldPolicy = {
  enabled: boolean;
  isVisible: (fieldName: string) => boolean;
  isLocked: (fieldName: string) => boolean;
  isRequired: (fieldName: string, fallback?: boolean) => boolean;
};

function createPolicy(enabled: boolean): EmbryoFieldPolicy {
  return {
    enabled,
    isVisible: (fieldName) => !enabled || allowedFields.has(fieldName),
    isLocked: (fieldName) => enabled && lockedFields.has(fieldName),
    isRequired: (fieldName, fallback = false) =>
      enabled ? requiredFields.has(fieldName) : fallback,
  };
}

const ENABLED_POLICY = createPolicy(true);
const DISABLED_POLICY = createPolicy(false);

type UseEmbryoFieldPolicyArgs = {
  isOutsideEmbryo?: boolean;
};

/**
 * True when either embryo-fields flag is on. Gates which controls are *built*.
 * `Add_Remove_Embryo_Fields` is mid-release and already true in dev/QA;
 * `Final_Embryo_Datapoints` supersedes it and works standalone.
 */
export function useEmbryoFieldsEnabled(): boolean {
  const addRemoveEmbryoFields = useFeatureFlag('Add_Remove_Embryo_Fields');
  const finalEmbryoDatapoints = useFeatureFlag('Final_Embryo_Datapoints');
  return addRemoveEmbryoFields || finalEmbryoDatapoints;
}

export function useEmbryoFieldPolicy({
  isOutsideEmbryo = false,
}: UseEmbryoFieldPolicyArgs = {}): EmbryoFieldPolicy {
  const flagEnabled = useFeatureFlag('Final_Embryo_Datapoints');
  // Outside embryos are a different record shape with their own explicit
  // field lists, and their edit flow exists to set donor/sire/name — the
  // workbook's locked and required rules would make it unusable.
  return flagEnabled && !isOutsideEmbryo ? ENABLED_POLICY : DISABLED_POLICY;
}

/**
 * Fields the V5 main-grid endpoint returns. Generated from
 * `TogApi/Dto/EmbryoGridV5Response.cs` — the backend's implementation of the
 * workbook's `Main Grid = Yes` set. The grid cannot render a column the
 * endpoint does not return, so this doubles as the grid allow-list.
 */
export const MAIN_GRID_FIELDS = [
  'billingOwnerId',
  'billingOwnerName',
  'calfType',
  'cane',
  'clientRecipient',
  'clinicRanchHaulIn',
  'createdOn',
  'creation',
  'donorBreedId',
  'donorBreedName',
  'donorId',
  'donorName',
  'donorNetMerit',
  'donorReg',
  'donorTpi',
  'embryoPa',
  'export',
  'exportTest1',
  'exportTest2',
  'exportTest3',
  'freezeMethodId',
  'freezeMethodName',
  'freezeTechId',
  'freezeTechName',
  'freezer',
  'freshFrozen',
  'frozenGrade',
  'frozenStage',
  'grade',
  'holsteinPtanetMerit',
  'id',
  'implantDate',
  'implantStatusCode',
  'method',
  'naabCode',
  'name',
  'ownerCode',
  'ownerId',
  'ownerName',
  'pc1Date',
  'pc1Res',
  'pc2Date',
  'pc2Res',
  'pc3Date',
  'pc3Res',
  'programName',
  'psi',
  'quantity',
  'receivedOn',
  'recipOwnerId',
  'recipOwnerName',
  'recipientId',
  'recipientTag',
  'semenColCode',
  'sex',
  'sexName',
  'shipToFbd',
  'sireBreedId',
  'sireBreedName',
  'siteName',
  'soldTo',
  'stage',
  'stateCode',
  'status',
  'statusName',
  'storageLocationId',
  'storageLocationName',
  'strawNumber',
  'tpi',
  'transferFarmLocationId',
  'transferFarmLocationName',
  'tt',
] as const;
